/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@/shared/ui/radix-icons';
import { useTranslation } from 'react-i18next';
import { PublicationCard } from './PublicationCard';
import { getPortalI18nKey } from '../types';
import type { PortalGroup, CatalogPublication } from '../types';
import { Text, Title, UnstyledButton } from '@/shared/ui';

interface Props {
  group: PortalGroup;
  onSelect: (entry: CatalogPublication) => void;
}

const SCROLL_AMOUNT = 260;

/*
 * Mask values for the scroll container edges.
 * Using CSS mask-image instead of absolutely-positioned gradient overlays
 * prevents the fade from painting over card border-radius corners.
 */
const MASK_NONE = 'none';
const MASK_LEFT = 'linear-gradient(to right, transparent 0px, black 48px)';
const MASK_RIGHT = 'linear-gradient(to left, transparent 0px, black 48px)';
const MASK_BOTH =
  'linear-gradient(to right, transparent 0px, black 48px, black calc(100% - 48px), transparent 100%)';

/**
 * Renders a horizontally scrollable lane of publication cards for one portal.
 *
 * @param props - The portal group and card selection handler.
 * @returns The portal lane element.
 */
export function PortalLane({ group, onSelect }: Props) {
  const { t } = useTranslation('@features/properties');
  const countLabel = t('publications.catalog.publicationCount', { count: group.entries.length });

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [updateArrows]);

  const SCROLL_BEHAVIOR = 'smooth' as ScrollBehavior;

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: SCROLL_BEHAVIOR });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: SCROLL_BEHAVIOR });
  };

  let maskImage = MASK_NONE;
  if (canScrollLeft && canScrollRight) maskImage = MASK_BOTH;
  else if (canScrollLeft) maskImage = MASK_LEFT;
  else if (canScrollRight) maskImage = MASK_RIGHT;

  return (
    <section data-testid={`portal-lane-${group.portal}`} className="flex flex-col gap-(--space-3)">
      {/* Lane header */}
      <div className="flex items-center justify-between px-(--space-1)">
        <Title order={3}
          className="m-0 font-bold leading-none"
          style={{ fontSize: 'clamp(13px, 1.4vw, 16px)', color: 'var(--text-primary)' }}
        >
          {t(getPortalI18nKey(group.portal), { defaultValue: group.label })}
        </Title>
        <Text component="span"
          className="text-xs shrink-0 ml-(--space-3)"
          style={{ color: 'var(--text-tertiary)' }}
        >
          {countLabel}
        </Text>
      </div>

      {/* Scroll container + floating arrow buttons */}
      <div className="relative">
        {/* Left arrow — floats above the faded edge, no background of its own */}
        {canScrollLeft && (
          <UnstyledButton
            type="button"
            aria-label={t('publications.catalog.scrollLeft')}
            onClick={scrollLeft}
            className="absolute left-1 top-0 bottom-3 z-10 flex items-center justify-center w-8 cursor-pointer border-none p-0 bg-transparent"
          >
            <Text component="span"
              className="flex items-center justify-center rounded-full w-7 h-7 shadow-sm bg-[color-mix(in_srgb,var(--surface-primary)_85%,transparent)] text-[var(--text-primary)] backdrop-blur-sm"
            >
              <ChevronLeftIcon width={16} height={16} />
            </Text>
          </UnstyledButton>
        )}

        {/* Right arrow */}
        {canScrollRight && (
          <UnstyledButton
            type="button"
            aria-label={t('publications.catalog.scrollRight')}
            onClick={scrollRight}
            className="absolute right-1 top-0 bottom-3 z-10 flex items-center justify-center w-8 cursor-pointer border-none p-0 bg-transparent"
          >
            <Text component="span"
              className="flex items-center justify-center rounded-full w-7 h-7 shadow-sm bg-[color-mix(in_srgb,var(--surface-primary)_85%,transparent)] text-[var(--text-primary)] backdrop-blur-sm"
            >
              <ChevronRightIcon width={16} height={16} />
            </Text>
          </UnstyledButton>
        )}

        {/* Horizontal scroll row — mask-image creates the fade without touching card corners */}
        <div
          ref={scrollRef}
          data-testid={`portal-lane-scroll-${group.portal}`}
          className="flex gap-(--space-3) overflow-x-auto pb-(--space-3) snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{
            paddingLeft: 4,
            paddingRight: 16,
            maskImage,
            WebkitMaskImage: maskImage,
          }}
        >
          {group.entries.map((entry) => (
            <PublicationCard
              key={`${entry.property_id}-${entry.portal}-${entry.publication_id}`}
              entry={entry}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
